import { useCallback } from 'react';
import { type Deal } from '@/types';
import { DealPinMap, IntelGlobe } from '@/components/viz';

interface DealMapProps {
  deals: Deal[];
  activeDeal: number;
  onDotClick: (index: number) => void;
  onDotHover: (index: number) => void;
  onDotLeave: () => void;
}

export function DealMap({
  deals,
  activeDeal,
  onDotClick,
  onDotHover,
  onDotLeave,
}: DealMapProps) {
  const hasLocationData = deals.some(
    (d) => d.lat !== undefined && d.lng !== undefined
  );
  const activeDealId =
    activeDeal >= 0 && activeDeal < deals.length ? deals[activeDeal].id : null;

  const handlePinHover = useCallback(
    (id: string | null) => {
      if (id === null) {
        onDotLeave();
        return;
      }
      const index = deals.findIndex((d) => d.id === id);
      if (index >= 0) onDotHover(index);
    },
    [deals, onDotHover, onDotLeave]
  );

  const handlePinClick = useCallback(
    (id: string) => {
      const index = deals.findIndex((d) => d.id === id);
      if (index >= 0) onDotClick(index);
    },
    [deals, onDotClick]
  );

  return (
    <div className="flex-1 flex flex-col border border-panel-border rounded-[3px] overflow-hidden min-h-0">
      {/* Header bar (NOT a <Panel>) — preserved verbatim from research § 4.11 */}
      <div className="flex items-center justify-between px-3.5 py-2 border-b border-panel-border bg-panel-bg shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px]">
            OPERATIONAL MAP
          </span>
          <span className="flex items-center gap-1 text-xs text-accent font-mono font-semibold">
            <span className="w-1 h-1 rounded-full bg-accent shadow-[0_0_6px_var(--color-accent),0_0_2px_var(--color-accent)] animate-pulse-status" />
            {deals.length} ACTIVE
          </span>
          {!hasLocationData && deals.length > 0 && (
            <span className="text-micro text-text-dim font-mono tracking-[0.3px]">
              (estimated positions)
            </span>
          )}
        </div>
        {activeDeal >= 0 && activeDeal < deals.length && (
          <div className="flex items-center gap-3">
            {/* C5: lat/lng readout when coords available (Lovable PipelinePage:195-198) */}
            {deals[activeDeal].lat != null && deals[activeDeal].lng != null && (
              <span className="text-xs font-mono text-text-muted">
                {deals[activeDeal].lat!.toFixed(2)}°N{' '}
                {Math.abs(deals[activeDeal].lng!).toFixed(2)}°W
              </span>
            )}
            {deals[activeDeal].market && (
              <span className="text-xs font-mono text-text-bright font-medium">
                {deals[activeDeal].market}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Map body — DealPinMap + IntelGlobe corner ornament.
          MUST be `flex flex-col` — <DealPinMap>'s root is `flex-1 … min-h-0`
          and only clamps the SVG (`height="100%"`) to this area when the
          parent is a flex container. Without it the SVG renders at its
          intrinsic ~750px and the map image is clipped (2026-05-18). */}
      <div className="flex flex-col flex-1 min-h-0 relative">
        <DealPinMap
          deals={deals}
          activeDealId={activeDealId}
          onPinHover={handlePinHover}
          onPinClick={handlePinClick}
          footerVariant="none"
          emptyVariant="bare"
        />
        <IntelGlobe id="pipe" size={42} bottom={12} right={14} />
      </div>
    </div>
  );
}
